import { ConfirmPrimitiveReservationInput } from '../../types/cart'

/** Every field the forms dereference on a booker or a guest profile. */
const PERSON_STRING_FIELDS = [
  'firstName',
  'lastName',
  'email',
  'identificationNo',
  'mobile'
]

const isPlainObject = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value)

/**
 * A person is read field by field: `booker.firstName.length`, regex `.test(...)`,
 * `identificationNo.slice(0, 10)`. Present-but-hollow is therefore not safe -- an object
 * with no firstName crashes exactly the way a missing object does.
 */
const isUsablePerson = (value: unknown): boolean =>
  isPlainObject(value) &&
  PERSON_STRING_FIELDS.every((field) => typeof value[field] === 'string')

const hasStringFields = (
  value: unknown,
  fields: readonly string[]
): value is Record<string, unknown> =>
  isPlainObject(value) &&
  fields.every((field) => typeof value[field] === 'string')

/**
 * Parse a `confirmPrimitiveInput` written to localStorage by an earlier visit, returning
 * null when it cannot be used.
 *
 * The booking stepper persists this state and reads it back on the next visit, so a browser
 * may be holding an object written by an older build. It was previously adopted wholesale:
 *
 *   setConfirmPrimitiveInput(JSON.parse(confirmPrimitiveInputLocalStorage))
 *
 * which replaced the initialised state with whatever happened to be stored. When that object
 * had no `confirmRooms`, the reserve step threw on
 * `confirmPrimitiveInput.confirmRooms.slice(...)` and the screen went blank.
 *
 * What is checked, and why none of it is optional:
 *
 *   - the two collections AND every person inside them, because the forms index into them
 *     during render. This is the crash the guard exists to prevent, and a shallow check that
 *     accepted `booker: {}` would let the same TypeError back in through a side door.
 *   - the scalars the server declares non-null (`walletID: ID!`, `cartID: String!`,
 *     `reservationNumber: String!`, `pmsRequestedMethod`, `grpcInput: GrpcInput!`). A restored
 *     object missing one reaches the mutation and comes back a GraphQL validation error, which
 *     renders as a generic "Something went wrong" -- indistinguishable from "your booking
 *     failed", and an invitation to retry something that may already have succeeded.
 *
 * Presence and type only, never emptiness: `reservationNumber` and `walletID` are legitimately
 * '' until the reserve step fills them in.
 *
 * Nothing beyond that is checked. Being stricter would discard usable state every time an
 * unrelated optional field is added, which is its own bug.
 */
export function parseStoredConfirmPrimitiveInput(
  raw: string | null | undefined
): ConfirmPrimitiveReservationInput | null {
  if (!raw) {
    return null
  }

  let parsed: unknown
  try {
    parsed = JSON.parse(raw)
  } catch {
    // Truncated or hand-edited storage. Nothing to recover.
    return null
  }

  if (!isPlainObject(parsed)) {
    return null
  }

  const roomsUsable =
    Array.isArray(parsed.confirmRooms) &&
    parsed.confirmRooms.every(
      (room) =>
        isPlainObject(room) &&
        typeof room.crsNumber === 'string' &&
        Array.isArray(room.profiles) &&
        room.profiles.every(isUsablePerson)
    )
  if (!roomsUsable) {
    return null
  }

  if (!isUsablePerson(parsed.booker)) {
    return null
  }

  const scalarsUsable =
    typeof parsed.reservationNumber === 'string' &&
    typeof parsed.pmsRequestedMethod === 'string' &&
    typeof parsed.walletID === 'string' &&
    typeof parsed.cartID === 'string' &&
    hasStringFields(parsed.grpcInput, [
      'channelCode',
      'propertyCode',
      'providerCode'
    ])
  if (!scalarsUsable) {
    return null
  }

  return parsed as unknown as ConfirmPrimitiveReservationInput
}
